import type { FlowModel, FlowNode, NodeShape } from './model';
import type { GeoEdge, GeoGroup, GeoItem, GeoNode, Geometry, Measure } from './geometry';
import { strokeDash } from './style-util';
import { THEME } from './theme';

/**
 * Layered layout for flowcharts (Sugiyama style, docs/ru/DSKHEMA.md §6):
 * break cycles → layers by longest path → order layers by barycenters (groups kept together)
 * → place along the cross axis with median alignment → smooth links through dummy points.
 * Computed for "down" in (cross, main) coordinates and mapped to the requested direction.
 */
const PAD_X = 16;
const PAD_Y = 10;
const RANK_GAP = 72;
const NODE_GAP = 48;
const GROUP_PAD = 18;
const GROUP_TITLE = 24;
const MARGIN = 10;
const LINE_H = 1.3;

interface LNode {
  idx: number;
  /** Real node, or null for a dummy point of a long link. */
  node: FlowNode | null;
  w: number;
  h: number;
  /** Size along the layer axis (main) and across it (cross). */
  main: number;
  cross: number;
  layer: number;
  order: number;
  c: number;
  m: number;
  group: string | null;
}

interface LEdge {
  from: number;
  to: number;
  /** Indices into the model's edge list. */
  model: number;
  reversed: boolean;
}

function nodeSize(n: FlowNode, measure: Measure): { w: number; h: number } {
  const font = THEME.font;
  const lines = n.label.split('\n');
  const tw = Math.max(...lines.map((l) => measure(l, font, false)));
  const th = lines.length * font * LINE_H;
  let w = Math.max(84, tw + PAD_X * 2);
  let h = Math.max(42, th + PAD_Y * 2);
  const s: NodeShape = n.shape;
  switch (s) {
    case 'diamond': {
      w = Math.max(110, (tw + PAD_X) * 1.55);
      h = Math.max(64, (th + PAD_Y) * 1.8);
      break;
    }
    case 'circle': {
      const d = Math.max(64, Math.hypot(tw, th) + PAD_Y * 2);
      w = h = d;
      break;
    }
    case 'ellipse':
      w *= 1.22;
      h *= 1.2;
      break;
    case 'hexagon':
    case 'parallelogram':
      w += h * 0.55;
      break;
    case 'pill':
      w += h * 0.4;
      break;
    case 'cylinder':
      h += 18;
      break;
    case 'document':
      h += 10;
      break;
    case 'manual':
      h += h * 0.15;
      break;
    case 'data':
      w += h * 0.35;
      break;
    case 'bracket':
    case 'brace':
      w += Math.min(h * 0.14, 24);
      break;
    case 'display':
      w += Math.min(h * 0.25, 40);
      break;
    case 'triangle':
      w = Math.max(w, h * 1.1);
      break;
    default:
      if (s.startsWith('module:')) {
        w = Math.max(w, 110);
        h = Math.max(h, 90);
      }
  }
  return { w: Math.round(w), h: Math.round(h) };
}

/** Default colors by form (decision, start/end, storage), overridden by the node style. */
function nodeColors(n: FlowNode): { fill: string; stroke: string; text: string } {
  const base: { fill: string; stroke: string } =
    n.shape === 'diamond' ? THEME.decision : n.shape === 'circle' || n.shape === 'pill' ? THEME.terminal : n.shape === 'cylinder' ? THEME.storage : THEME.node;
  return { fill: n.style.fill ?? base.fill, stroke: n.style.stroke ?? base.stroke, text: n.style.text ?? THEME.node.text };
}

/** Group path from the outermost group to the node's own group. */
function groupPath(model: FlowModel, g: string | null): string[] {
  const out: string[] = [];
  const parent = new Map(model.groups.map((x) => [x.id, x.parent]));
  for (let cur = g, guard = 0; cur && guard < 64; cur = parent.get(cur) ?? null, guard++) out.unshift(cur);
  return out;
}

function commonGroup(a: string[], b: string[]): string | null {
  let g: string | null = null;
  for (let i = 0; i < Math.min(a.length, b.length) && a[i] === b[i]; i++) g = a[i];
  return g;
}

export function layoutFlow(model: FlowModel, measure: Measure): Geometry {
  const vertical = model.direction === 'down' || model.direction === 'up';
  const nodes: LNode[] = model.nodes.map((n, idx) => {
    const { w, h } = n.size ?? nodeSize(n, measure);
    return { idx, node: n, w, h, main: vertical ? h : w, cross: vertical ? w : h, layer: 0, order: 0, c: 0, m: 0, group: n.group };
  });
  const index = new Map(model.nodes.map((n, i) => [n.id, i]));
  const paths = nodes.map((n) => groupPath(model, n.group));

  // ---- break cycles (DFS in declaration order; back edges are reversed for layering)
  const edges: LEdge[] = [];
  const out = nodes.map(() => [] as number[]);
  model.edges.forEach((e, i) => {
    const a = index.get(e.from)!;
    const b = index.get(e.to)!;
    if (a === b) return; // self loops are drawn separately
    edges.push({ from: a, to: b, model: i, reversed: false });
    out[a].push(edges.length - 1);
  });
  const state = new Uint8Array(nodes.length);
  for (let s = 0; s < nodes.length; s++) {
    if (state[s]) continue;
    const stack: [number, number][] = [[s, 0]];
    state[s] = 1;
    while (stack.length) {
      const top = stack[stack.length - 1];
      const [u, k] = top;
      if (k >= out[u].length) {
        state[u] = 2;
        stack.pop();
        continue;
      }
      top[1]++;
      const e = edges[out[u][k]];
      const v = e.to;
      if (state[v] === 1) e.reversed = true;
      else if (state[v] === 0) {
        state[v] = 1;
        stack.push([v, 0]);
      }
    }
  }
  const dag = edges.map((e) => (e.reversed ? { a: e.to, b: e.from, e } : { a: e.from, b: e.to, e }));

  // ---- layers: longest path from the sources
  const indeg = new Array<number>(nodes.length).fill(0);
  const succ = nodes.map(() => [] as number[]);
  for (const d of dag) {
    indeg[d.b]++;
    succ[d.a].push(d.b);
  }
  const queue: number[] = [];
  indeg.forEach((d, i) => d === 0 && queue.push(i));
  for (let qi = 0; qi < queue.length; qi++) {
    const u = queue[qi];
    for (const v of succ[u]) {
      nodes[v].layer = Math.max(nodes[v].layer, nodes[u].layer + 1);
      if (--indeg[v] === 0) queue.push(v);
    }
  }
  // pull sources down next to their nearest successor, so side branches do not stretch to the top
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (dag.some((d) => d.b === i) || !succ[i].length) continue;
    const minSucc = Math.min(...succ[i].map((v) => nodes[v].layer));
    n.layer = Math.max(n.layer, minSucc - 1);
  }

  // ---- dummy points for links spanning several layers
  const orthogonal = model.links !== 'curved';
  /**
   * Angular back links (cycles) over several layers loop around the outside instead of cutting
   * through the flow; between neighbouring layers a short link straight up the gap is clearer.
   */
  const aroundSide = (e: LEdge) => orthogonal && e.reversed && Math.abs(nodes[e.from].layer - nodes[e.to].layer) > 1;
  const all: LNode[] = [...nodes];
  const chains: { e: LEdge; seq: number[] }[] = [];
  for (const d of dag) {
    const seq = [d.a];
    for (let l = nodes[d.a].layer + 1; l < nodes[d.b].layer && !aroundSide(d.e); l++) {
      const g = commonGroup(paths[d.a], paths[d.b]);
      const dummy: LNode = { idx: all.length, node: null, w: 0, h: 0, main: 0, cross: 10, layer: l, order: 0, c: 0, m: 0, group: g };
      all.push(dummy);
      paths.push(groupPath(model, g));
      seq.push(dummy.idx);
    }
    seq.push(d.b);
    chains.push({ e: d.e, seq });
  }
  const up = all.map(() => [] as number[]);
  const down = all.map(() => [] as number[]);
  for (const ch of chains) {
    if (aroundSide(ch.e)) continue;
    for (let i = 0; i + 1 < ch.seq.length; i++) {
      down[ch.seq[i]].push(ch.seq[i + 1]);
      up[ch.seq[i + 1]].push(ch.seq[i]);
    }
  }

  // ---- order inside layers: barycenters, groups kept contiguous
  const layerCount = Math.max(0, ...all.map((n) => n.layer)) + 1;
  const layers: number[][] = Array.from({ length: layerCount }, () => []);
  for (const n of all) layers[n.layer].push(n.idx);
  layers.forEach((l) => l.forEach((v, i) => (all[v].order = i)));
  const sortLayer = (l: number[], bary: (v: number) => number) => {
    const key = new Map(l.map((v) => [v, bary(v)]));
    // cluster barycenter = mean of its members at each group depth
    const depthKey = (v: number, depth: number): number => {
      const g = paths[v][depth];
      if (g === undefined) return key.get(v)!;
      const members = l.filter((x) => paths[x][depth] === g);
      return members.reduce((s, x) => s + key.get(x)!, 0) / members.length;
    };
    l.sort((a, b) => {
      const da = paths[a];
      const db = paths[b];
      for (let d = 0; d < Math.max(da.length, db.length); d++) {
        if (da[d] === db[d]) continue;
        const ka = depthKey(a, d);
        const kb = depthKey(b, d);
        if (ka !== kb) return ka - kb;
        return (da[d] ?? '').localeCompare(db[d] ?? '');
      }
      return key.get(a)! - key.get(b)!;
    });
    l.forEach((v, i) => (all[v].order = i));
  };
  const pairCrossings = (li: number): number => {
    if (li < 0 || li + 1 >= layerCount) return 0;
    const links: [number, number][] = [];
    for (const v of layers[li + 1]) for (const u of up[v]) links.push([all[u].order, all[v].order]);
    let n = 0;
    for (let i = 0; i < links.length; i++) for (let j = i + 1; j < links.length; j++) if ((links[i][0] - links[j][0]) * (links[i][1] - links[j][1]) < 0) n++;
    return n;
  };
  /** Crossings among the links of u (placed left) and v (placed right), above and below their layer. */
  const crossBetween = (u: number, v: number): number => {
    let n = 0;
    for (const a of up[u]) for (const b of up[v]) if (all[a].order > all[b].order) n++;
    for (const a of down[u]) for (const b of down[v]) if (all[a].order > all[b].order) n++;
    return n;
  };
  const totalCrossings = () => layers.reduce((s, _, li) => s + pairCrossings(li), 0);
  const setOrder = (src: number[][]) =>
    src.forEach((l, li) => {
      layers[li] = l.slice();
      layers[li].forEach((v, i) => (all[v].order = i));
    });
  /** Barycenter sweeps, then transpose; returns the crossing count of the order it leaves. */
  const runOrdering = (init: number[][]): number => {
    setOrder(init);
    for (let it = 0; it < 12; it++) {
      const goingDown = it % 2 === 0;
      const range = goingDown ? layers.map((_, i) => i).slice(1) : layers.map((_, i) => i).slice(0, -1).reverse();
      for (const li of range) {
        sortLayer(layers[li], (v) => {
          const nb = goingDown ? up[v] : down[v];
          return nb.length ? nb.reduce((s, u) => s + all[u].order, 0) / nb.length : all[v].order;
        });
      }
    }
    // transpose: swap neighbours in a layer (same group only) while that reduces crossings with the
    // layers above and below; odd passes also take sideways swaps (same count) next to crossings, to
    // step out of local minima where two swaps are needed at once; the best order seen wins
    let bestCount = totalCrossings();
    let bestOrder = layers.map((l) => l.slice());
    for (let pass = 0; pass < 16 && bestCount > 0; pass++) {
      layers.forEach((l) => {
        for (let i = 0; i + 1 < l.length; i++) {
          if (paths[l[i]].join('/') !== paths[l[i + 1]].join('/')) continue;
          // only the links of the two nodes can change: compare them left/right against right/left
          const before = crossBetween(l[i], l[i + 1]);
          const after = crossBetween(l[i + 1], l[i]);
          if (after < before || (pass % 2 === 1 && after === before && before > 0)) {
            [l[i], l[i + 1]] = [l[i + 1], l[i]];
            all[l[i]].order = i;
            all[l[i + 1]].order = i + 1;
          }
        }
      });
      const c = totalCrossings();
      if (c < bestCount) {
        bestCount = c;
        bestOrder = layers.map((l) => l.slice());
      }
    }
    setOrder(bestOrder);
    return bestCount;
  };
  // declaration order first (stable, familiar result); while crossings remain, a few more starting
  // orders (reversed, then seeded shuffles — the layout stays deterministic) and the best one wins
  const base = layers.map((l) => l.slice());
  let best = runOrdering(base);
  let bestLayers = layers.map((l) => l.slice());
  let seed = 7;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const tries = all.length <= 40 ? 16 : all.length <= 80 ? 6 : all.length <= 150 ? 2 : 0;
  for (let t = 0; t < tries && best > 0; t++) {
    const init = base.map((l) => {
      const c = l.slice();
      if (t === 0) return c.reverse();
      for (let i = c.length - 1; i > 0; i--) {
        const j = Math.floor(rnd() * (i + 1));
        [c[i], c[j]] = [c[j], c[i]];
      }
      return c;
    });
    const c = runOrdering(init);
    if (c < best) {
      best = c;
      bestLayers = layers.map((l) => l.slice());
    }
  }
  setOrder(bestLayers);

  // ---- main axis: layer bands; gaps grow for link labels
  const labelOf = (i: number) => model.edges[i].label;
  // groups need room for their padding and title between layers
  const gapAfter = new Array<number>(layerCount).fill(RANK_GAP + (model.groups.length ? GROUP_PAD + GROUP_TITLE : 0));
  for (const ch of chains) {
    const text = labelOf(ch.e.model);
    if (!text) continue;
    const lines = text.split('\n');
    const along = vertical ? lines.length * THEME.smallFont * LINE_H + 10 : Math.max(...lines.map((x) => measure(x, THEME.smallFont, false))) + 16;
    // the label sits on the last leg into the target, which gets ~60% of the gap
    const l = all[ch.seq[ch.seq.length - 2]].layer;
    gapAfter[l] = Math.max(gapAfter[l], (along + 16) / 0.6);
  }
  let m = 0;
  const bandMain: number[] = [];
  /** Main-axis end of each layer band (the gap after it runs to the next band). */
  const bandEnd: number[] = [];
  for (let l = 0; l < layerCount; l++) {
    const thick = Math.max(0, ...layers[l].map((v) => all[v].main));
    bandMain.push(m + thick / 2);
    bandEnd.push(m + thick);
    m += thick + gapAfter[l];
  }
  for (const n of all) n.m = bandMain[n.layer];

  // ---- cross axis: pack, then align with neighbour medians
  const sep = (a: number, b: number) => {
    const na = all[a];
    const nb = all[b];
    const extra = paths[a].join('/') !== paths[b].join('/') ? GROUP_PAD : 0;
    return na.cross / 2 + nb.cross / 2 + (na.node && nb.node ? NODE_GAP : 14) + extra;
  };
  for (const l of layers) {
    let c = 0;
    l.forEach((v, i) => {
      if (i > 0) c += sep(l[i - 1], v);
      all[v].c = c;
    });
  }
  const median = (xs: number[]) => {
    const s = [...xs].sort((a, b) => a - b);
    const k = s.length >> 1;
    return s.length % 2 ? s[k] : (s[k - 1] + s[k]) / 2;
  };
  for (let it = 0; it < 10; it++) {
    const goingDown = it % 2 === 0;
    const order = goingDown ? layers : [...layers].reverse();
    for (const l of order) {
      const want = l.map((v) => {
        const nb = [...(goingDown ? up[v] : down[v]), ...(it > 4 ? (goingDown ? down[v] : up[v]) : [])];
        return nb.length ? median(nb.map((u) => all[u].c)) : all[v].c;
      });
      // left to right: at least the separation; then right to left towards the wish
      const pos = want.slice();
      for (let i = 1; i < l.length; i++) pos[i] = Math.max(pos[i], pos[i - 1] + sep(l[i - 1], l[i]));
      for (let i = l.length - 2; i >= 0; i--) pos[i] = Math.min(pos[i], pos[i + 1] - sep(l[i], l[i + 1]), Math.max(want[i], pos[i]));
      for (let i = 1; i < l.length; i++) pos[i] = Math.max(pos[i], pos[i - 1] + sep(l[i - 1], l[i]));
      l.forEach((v, i) => (all[v].c = pos[i]));
    }
  }
  // long links: put all their waypoints on one line where the neighbours leave room, so the link
  // runs straight instead of stepping sideways at every layer (the cheapest such line wins)
  for (const ch of chains) {
    const dummies = ch.seq.slice(1, -1);
    if (dummies.length < 2) continue;
    const range = (v: number): [number, number] => {
      const l = layers[all[v].layer];
      const i = l.indexOf(v);
      return [i > 0 ? all[l[i - 1]].c + sep(l[i - 1], v) : -Infinity, i + 1 < l.length ? all[l[i + 1]].c - sep(v, l[i + 1]) : Infinity];
    };
    const ranges = dummies.map(range);
    const lo = Math.max(...ranges.map((r) => r[0]));
    const hi = Math.min(...ranges.map((r) => r[1]));
    if (lo > hi) continue;
    const ends = [all[ch.seq[0]].c, all[ch.seq[ch.seq.length - 1]].c];
    let bestC = NaN;
    let bestCost = Infinity;
    for (const c of [...dummies.map((v) => all[v].c), ...ends, lo, hi]) {
      if (c < lo || c > hi) continue;
      const cost = dummies.reduce((s, v) => s + Math.abs(all[v].c - c), 0) + Math.abs(ends[0] - c) + Math.abs(ends[1] - c);
      if (cost < bestCost) {
        bestCost = cost;
        bestC = c;
      }
    }
    if (!Number.isNaN(bestC)) for (const v of dummies) all[v].c = bestC;
  }

  // ---- geometry in (cross, main) → (x, y)
  const minC = Math.min(...all.map((n) => n.c - n.cross / 2));
  const maxM = Math.max(...all.map((n) => n.m + n.main / 2));
  const titleRoom = model.groups.length ? GROUP_TITLE + GROUP_PAD : 0;
  const toXY = (c: number, mm: number): [number, number] => {
    const cc = c - minC + MARGIN + (model.groups.length ? GROUP_PAD : 0);
    const mmm = model.direction === 'up' || model.direction === 'left' ? maxM - mm : mm;
    const mm2 = mmm + MARGIN + titleRoom;
    return vertical ? [cc, mm2] : [mm2, cc];
  };
  const items: GeoItem[] = [];
  const geoNodes = new Map<string, GeoNode>();
  for (const n of nodes) {
    const [cx, cy] = toXY(n.c, n.m);
    const f = n.node!;
    const col = nodeColors(f);
    const g: GeoNode = {
      k: 'node',
      id: f.id,
      shape: f.shape,
      x: cx - n.w / 2,
      y: cy - n.h / 2,
      w: n.w,
      h: n.h,
      label: f.label,
      ...col,
      width: f.style.width ?? THEME.node.width,
      strokeDash: strokeDash(f.style),
      fontSize: THEME.font,
      flip: !!f.style.flip,
    };
    geoNodes.set(f.id, g);
  }

  // ---- groups: frames around members (deepest first so parents enclose children)
  const groupItems: GeoGroup[] = [];
  const boxes = new Map<string, { x0: number; y0: number; x1: number; y1: number }>();
  const depth = (id: string) => groupPath(model, id).length;
  const grow = (id: string, x0: number, y0: number, x1: number, y1: number) => {
    const b = boxes.get(id) ?? { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
    boxes.set(id, { x0: Math.min(b.x0, x0), y0: Math.min(b.y0, y0), x1: Math.max(b.x1, x1), y1: Math.max(b.y1, y1) });
  };
  for (const g of geoNodes.values()) {
    const node = model.nodes[index.get(g.id)!];
    for (const gid of groupPath(model, node.group)) grow(gid, g.x, g.y, g.x + g.w, g.y + g.h);
  }
  for (const grp of [...model.groups].sort((a, b) => depth(b.id) - depth(a.id))) {
    const b = boxes.get(grp.id);
    if (!b) continue;
    const pad = GROUP_PAD;
    const box = { x0: b.x0 - pad, y0: b.y0 - pad - GROUP_TITLE, x1: b.x1 + pad, y1: b.y1 + pad };
    if (grp.parent) grow(grp.parent, box.x0, box.y0, box.x1, box.y1);
    groupItems.push({ k: 'group', id: grp.id, title: grp.title, x: box.x0, y: box.y0, w: box.x1 - box.x0, h: box.y1 - box.y0, fill: THEME.group.fill, stroke: THEME.group.stroke, titleH: GROUP_TITLE, fontSize: THEME.smallFont });
  }
  groupItems.sort((a, b) => depth(a.id) - depth(b.id));
  // link labels keep clear of nodes, group titles and each other
  const avoid: Rect[] = [...geoNodes.values()].map((g) => ({ x: g.x, y: g.y, w: g.w, h: g.h }));
  for (const g of groupItems) if (g.title) avoid.push({ x: g.x + 8, y: g.y + 2, w: measure(g.title, g.fontSize, true) + 12, h: g.titleH });

  // ---- links: smooth curves through dummy points, ports spread along the node side
  // port order on each node side: towards the other end of the link at first (refined below)
  const outPorts = new Map<number, number[]>();
  const inPorts = new Map<number, number[]>();
  chains.forEach((ch, ci) => {
    const s = ch.seq;
    if (aroundSide(ch.e)) return;
    (outPorts.get(s[0]) ?? outPorts.set(s[0], []).get(s[0])!).push(ci);
    (inPorts.get(s[s.length - 1]) ?? inPorts.set(s[s.length - 1], []).get(s[s.length - 1])!).push(ci);
  });
  for (const list of outPorts.values()) list.sort((a, b) => all[chains[a].seq[1]].c - all[chains[b].seq[1]].c);
  for (const list of inPorts.values()) list.sort((a, b) => all[chains[a].seq[chains[a].seq.length - 2]].c - all[chains[b].seq[chains[b].seq.length - 2]].c);
  const portOffset = (node: number, ci: number, map: Map<number, number[]>) => {
    const list = map.get(node) ?? [];
    const n = all[node];
    const shape = n.node?.shape;
    if (list.length < 2 || shape === 'diamond' || shape === 'circle' || shape === 'ellipse') return 0;
    const step = Math.min(16, n.cross / (list.length + 1));
    return (list.indexOf(ci) - (list.length - 1) / 2) * step;
  };
  const outPort = (ci: number) => portOffset(chains[ci].seq[0], ci, outPorts);
  const inPort = (ci: number) => portOffset(chains[ci].seq[chains[ci].seq.length - 1], ci, inPorts);

  // orthogonal links: each gap between layers is split into channels, one per link crossing it,
  // so horizontal runs of different links never lie on top of each other
  const channels = new Map<string, number>();
  const perGap = new Map<number, { ci: number; seg: number }[]>();
  const placeChannels = () => {
    for (const [l, list] of perGap) {
      const start = bandEnd[l];
      const span = Math.max(1, bandMain[l + 1] - all[layers[l + 1][0]].main / 2 - start);
      const lane = Math.min(10, span / (list.length + 1));
      list.forEach((x, k) => channels.set(`${x.ci}:${x.seg}`, start + span * 0.36 + (k - (list.length - 1) / 2) * lane));
    }
  };
  if (orthogonal) {
    const ends = new Map<string, { c0: number; c1: number }>();
    chains.forEach((ch, ci) => {
      if (aroundSide(ch.e)) return;
      const s = ch.seq;
      for (let i = 0; i + 1 < s.length; i++) {
        const l = all[s[i]].layer;
        (perGap.get(l) ?? perGap.set(l, []).get(l)!).push({ ci, seg: i });
        // real port positions: links sharing a node leave and arrive at different points of its side
        ends.set(`${ci}:${i}`, { c0: all[s[i]].c + (i === 0 ? outPort(ci) : 0), c1: all[s[i + 1]].c + (i + 2 === s.length ? inPort(ci) : 0) });
      }
    });
    for (const list of perGap.values()) {
      // lane order that avoids crossings: of the links going right, the one starting further right
      // (or, from the same point, going further) takes the upper lane; mirrored for links going left;
      // straight ones are in the middle
      const at = (x: { ci: number; seg: number }) => ends.get(`${x.ci}:${x.seg}`)!;
      const dir = (x: { c0: number; c1: number }) => (x.c1 > x.c0 + 0.5 ? 1 : x.c1 < x.c0 - 0.5 ? -1 : 0);
      list.sort((p, q) => {
        const a = at(p);
        const b = at(q);
        const da = dir(a);
        const db = dir(b);
        if (da !== db) return db - da;
        if (da > 0) return b.c0 - a.c0 || b.c1 - a.c1;
        if (da < 0) return a.c0 - b.c0 || a.c1 - b.c1;
        return a.c0 - b.c0;
      });
    }
    placeChannels();
  }

  // back links loop around the outside of the layers they span, on the nearer side at first
  const backRight = new Map<number, boolean>();
  /** Blocked ends only: leave through the gap above the source / enter through the gap below the target. */
  const backGaps = new Map<number, { from: boolean; to: boolean }>();
  const backSpan = (ci: number) => {
    const top = all[chains[ci].seq[0]];
    const bottom = all[chains[ci].seq[chains[ci].seq.length - 1]];
    const span = all.filter((n) => n.node && n.layer >= top.layer && n.layer <= bottom.layer);
    return { top, bottom, lo: Math.min(...span.map((n) => n.c - n.cross / 2)), hi: Math.max(...span.map((n) => n.c + n.cross / 2)) };
  };
  chains.forEach((ch, ci) => {
    if (!aroundSide(ch.e)) return;
    const { top, bottom, lo, hi } = backSpan(ci);
    backRight.set(ci, (top.c + bottom.c) / 2 >= (lo + hi) / 2);
    backGaps.set(ci, { from: false, to: false });
  });

  /** Angular route of every link (polyline in x, y), from its real source to its real target. */
  const routeAll = (): number[][] => {
    const sideLanes = { left: 0, right: 0 };
    return chains.map((ch, ci) => {
      const flat: number[] = [];
      if (aroundSide(ch.e)) {
        const { top, bottom, lo, hi } = backSpan(ci);
        const right = backRight.get(ci)!;
        const k = right ? sideLanes.right++ : sideLanes.left++;
        const lane = right ? hi + 26 + k * 14 : lo - 26 - k * 14;
        const sign = right ? 1 : -1;
        // a neighbour in the same layer between the node and the lane: leave (or enter) through the
        // gap below (above) the layer instead of running through that neighbour
        const blocked = (n: LNode) => layers[n.layer].some((v) => v !== n.idx && all[v].node && (right ? all[v].c > n.c : all[v].c < n.c));
        // which gap (above or below the layer) is the search's choice: each crosses different links
        const above = (l: number) => 2 * bandMain[l] - bandEnd[l] - 14 - k * 6;
        const below = (l: number) => bandEnd[l] + 14 + k * 6;
        const pts: [number, number][] = [];
        if (blocked(bottom)) {
          const up = backGaps.get(ci)!.from;
          const c = bottom.c + (sign * bottom.cross) / 4;
          const m = up ? above(bottom.layer) : below(bottom.layer);
          pts.push([c, bottom.m + ((up ? -1 : 1) * bottom.main) / 2], [c, m], [lane, m]);
        } else pts.push([bottom.c + (sign * bottom.cross) / 2, bottom.m], [lane, bottom.m]);
        if (blocked(top)) {
          const down = backGaps.get(ci)!.to;
          const c = top.c + (sign * top.cross) / 4;
          const m = down ? below(top.layer) : above(top.layer);
          pts.push([lane, m], [c, m], [c, top.m + ((down ? 1 : -1) * top.main) / 2]);
        } else pts.push([lane, top.m], [top.c + (sign * top.cross) / 2, top.m]);
        for (const [c, mm] of pts) {
          const [x, y] = toXY(c, mm);
          flat.push(x, y);
        }
        return flat;
      }
      const s = ch.seq;
      const first = all[s[0]];
      const last = all[s[s.length - 1]];
      const isPointy = (n: LNode) => n.node?.shape === 'diamond' || n.node?.shape === 'circle' || n.node?.shape === 'ellipse';
      if (ch.e.reversed && s.length === 2 && isPointy(first) && (outPorts.get(s[0])?.length ?? 0) > 1) {
        // a short back link into a diamond whose bottom tip is taken by outgoing links: enter the
        // side tip facing the source instead of sharing that tip (listed from the target end)
        const sx = last.c + inPort(ci);
        const sign = sx >= first.c ? 1 : -1;
        const tip = first.c + (sign * first.cross) / 2;
        const sTop = last.m - last.main / 2;
        const covered = (x: number) => layers[first.layer].some((v) => all[v].node && Math.abs(all[v].c - x) < all[v].cross / 2 + 4);
        // just past the tip: leave the source right below the turn so the link is a single corner
        const lane = tip + sign * 18;
        const gap = channels.get(`${ci}:0`) ?? (first.m + sTop) / 2;
        const route: [number, number][] =
          sign * (sx - tip) > 8 && !covered(sx)
            ? [[tip, first.m], [sx, first.m], [sx, sTop]]
            : Math.abs(lane - last.c) < last.cross / 2 - 6 && !covered(lane)
              ? [[tip, first.m], [lane, first.m], [lane, sTop]]
              : [[tip, first.m], [lane, first.m], [lane, gap], [sx, gap], [sx, sTop]];
        for (const [c, mm] of route) {
          const [x, y] = toXY(c, mm);
          flat.push(x, y);
        }
        return simplifyStraight(flat);
      }
      const pts: [number, number][] = [[first.c + outPort(ci), first.m + first.main / 2]];
      for (let i = 1; i < s.length - 1; i++) pts.push([all[s[i]].c, all[s[i]].m]);
      pts.push([last.c + inPort(ci), last.m - last.main / 2]);
      // leave along the main axis, cross in this link's channel, arrive along the main axis
      const push = (c: number, mm: number) => {
        const [x, y] = toXY(c, mm);
        const n = flat.length;
        if (n >= 2 && Math.abs(flat[n - 2] - x) < 0.01 && Math.abs(flat[n - 1] - y) < 0.01) return;
        flat.push(x, y);
      };
      push(pts[0][0], pts[0][1]);
      for (let i = 0; i + 1 < pts.length; i++) {
        const [c0] = pts[i];
        const [c1, m1] = pts[i + 1];
        const mid = channels.get(`${ci}:${i}`) ?? (pts[i][1] + m1) / 2;
        const target = all[s[i + 1]];
        // a few pixels apart and the target is wide enough: go straight instead of a tiny step
        if (Math.abs(c1 - c0) < 14 && Math.abs(c0 - target.c) < target.cross / 2 - 4) {
          const pointy = target.node && (target.node.shape === 'diamond' || target.node.shape === 'circle' || target.node.shape === 'ellipse');
          const source = all[s[i]];
          if (pointy && i === 0 && Math.abs(target.c - source.c) < source.cross / 2 - 4) {
            // diamonds and circles are entered at their tip: move the start to line up with it
            const [x0, y0] = toXY(target.c, pts[0][1]);
            flat[0] = x0;
            flat[1] = y0;
            pts[i + 1] = [target.c, m1];
            push(target.c, m1);
            continue;
          }
          pts[i + 1] = [c0, m1];
          push(c0, m1);
          continue;
        }
        if (Math.abs(c1 - c0) > 0.5) {
          push(c0, mid);
          push(c1, mid);
        }
        push(c1, m1);
      }
      return simplifyStraight(flat);
    });
  };

  let routes: number[][] = [];
  if (orthogonal) {
    routes = routeAll();
    // local search on the drawn routes: swap neighbouring lanes in a gap, neighbouring ports on a
    // node side, or the side/gaps of a back link, and keep a change only when it lowers the cost —
    // crossings, plus running through a node, which is worse than crossing a link. Only the routes
    // a change actually moves are recounted.
    const nodeRects = nodes.map((n) => {
      const g = geoNodes.get(n.node!.id)!;
      return { idx: n.idx, x0: g.x + 2, y0: g.y + 2, x1: g.x + g.w - 2, y1: g.y + g.h - 2 };
    });
    const hitsOf = (ci: number, p: number[]) => {
      const s = chains[ci].seq;
      let hits = 0;
      for (const b of nodeRects) {
        if (b.idx === s[0] || b.idx === s[s.length - 1]) continue;
        for (let i = 0; i + 3 < p.length; i += 2) {
          if (Math.min(p[i], p[i + 2]) < b.x1 && Math.max(p[i], p[i + 2]) > b.x0 && Math.min(p[i + 1], p[i + 3]) < b.y1 && Math.max(p[i + 1], p[i + 3]) > b.y0) hits++;
        }
      }
      return hits;
    };
    let best = countCrossings(routes) + routes.reduce((s, p, ci) => s + hitsOf(ci, p) * 4, 0);
    const same = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) < 0.01);
    const swapLists: (number | { ci: number; seg: number })[][] = [...perGap.values(), ...outPorts.values(), ...inPorts.values()].filter((l) => l.length > 1);
    const idOf = (x: number | { ci: number }) => (typeof x === 'number' ? x : x.ci);
    /** Links with a crossing or running through a node: only changes next to them are tried. */
    const badRoutes = () => {
      const bad = new Set<number>();
      routes.forEach((p, i) => {
        if (hitsOf(i, p)) bad.add(i);
        for (let j = i + 1; j < routes.length; j++) {
          if (polyCrossings(p, routes[j])) {
            bad.add(i);
            bad.add(j);
          }
        }
      });
      return bad;
    };
    // work budget: each attempt re-routes every link, so big schemes get fewer attempts
    let budget = Math.floor(12000 / Math.max(1, chains.length));
    for (let pass = 0; pass < 6 && best > 0 && budget > 0; pass++) {
      const before = best;
      const bad = badRoutes();
      const attempt = (apply: () => void, undo: () => void) => {
        if (best === 0 || budget-- <= 0) return;
        apply();
        placeChannels();
        const r = routeAll();
        const changed = r.map((p, i) => (same(p, routes[i]) ? -1 : i)).filter((i) => i >= 0);
        const moved = new Set(changed);
        let delta = 0;
        for (const i of changed) {
          delta += (hitsOf(i, r[i]) - hitsOf(i, routes[i])) * 4;
          for (let j = 0; j < r.length; j++) {
            if (j === i || (moved.has(j) && j < i)) continue;
            delta += polyCrossings(r[i], r[j]) - polyCrossings(routes[i], routes[j]);
          }
        }
        if (changed.length && delta < 0) {
          best += delta;
          routes = r;
        } else {
          undo();
          placeChannels();
        }
      };
      for (const list of swapLists) {
        for (let i = 0; i + 1 < list.length; i++) {
          if (!bad.has(idOf(list[i])) && !bad.has(idOf(list[i + 1]))) continue;
          const swap = () => {
            const t = list[i];
            list[i] = list[i + 1];
            list[i + 1] = t;
          };
          attempt(swap, swap);
        }
      }
      for (const ci of backRight.keys()) {
        if (!bad.has(ci)) continue;
        const flip = () => backRight.set(ci, !backRight.get(ci));
        attempt(flip, flip);
        const gaps = backGaps.get(ci)!;
        const flipFrom = () => (gaps.from = !gaps.from);
        const flipTo = () => (gaps.to = !gaps.to);
        attempt(flipFrom, flipFrom);
        attempt(flipTo, flipTo);
      }
      if (best >= before) break;
    }
  }

  const edgeItems: GeoEdge[] = [];
  /** Thin boxes around the segments of every other link: labels keep off their lines and corners. */
  const lineRects = (own: number): Rect[] =>
    routes.flatMap((p, ci) => {
      if (ci === own) return [];
      const out: Rect[] = [];
      for (let i = 0; i + 3 < p.length; i += 2) out.push({ x: Math.min(p[i], p[i + 2]) - 4, y: Math.min(p[i + 1], p[i + 3]) - 4, w: Math.abs(p[i + 2] - p[i]) + 8, h: Math.abs(p[i + 3] - p[i + 1]) + 8 });
      return out;
    });
  chains.forEach((ch, ci) => {
    const e = model.edges[ch.e.model];
    if (orthogonal) {
      const r = routes[ci];
      edgeItems.push(edgeGeo(e, ch.e.reversed && !aroundSide(ch.e) ? reversePoints(r) : r, measure, true, avoid, lineRects(ci)));
      return;
    }
    const s = ch.seq;
    const first = all[s[0]];
    const last = all[s[s.length - 1]];
    const pts: [number, number][] = [[first.c + outPort(ci), first.m + first.main / 2]];
    for (let i = 1; i < s.length - 1; i++) pts.push([all[s[i]].c, all[s[i]].m]);
    pts.push([last.c + inPort(ci), last.m - last.main / 2]);
    const flat: number[] = [];
    // cubic segments with tangents along the main axis
    for (let i = 0; i + 1 < pts.length; i++) {
      const [c0, m0] = pts[i];
      const [c1, m1] = pts[i + 1];
      const dm = (m1 - m0) / 2;
      const steps = Math.abs(c1 - c0) < 1 ? 1 : 14;
      for (let k = i === 0 ? 0 : 1; k <= steps; k++) {
        const t = k / steps;
        const u = 1 - t;
        const c = u * u * u * c0 + 3 * u * u * t * c0 + 3 * u * t * t * c1 + t * t * t * c1;
        const mm = u * u * u * m0 + 3 * u * u * t * (m0 + dm) + 3 * u * t * t * (m1 - dm) + t * t * t * m1;
        const [x, y] = toXY(c, mm);
        flat.push(x, y);
      }
    }
    edgeItems.push(edgeGeo(e, ch.e.reversed ? reversePoints(flat) : flat, measure, false, avoid));
  });
  // self loops: a small loop on a cross side — of the two, the one crossing fewer links
  const placed = [...routes];
  model.edges.forEach((e) => {
    if (e.from !== e.to) return;
    const g = geoNodes.get(e.from)!;
    const r = Math.min(26, Math.max(16, (vertical ? g.h : g.w) / 2.5));
    const loop = (far: boolean): number[] => {
      if (orthogonal) {
        const out = 30;
        return vertical
          ? far
            ? [g.x + g.w, g.y + g.h * 0.25, g.x + g.w + out, g.y + g.h * 0.25, g.x + g.w + out, g.y + g.h * 0.75, g.x + g.w, g.y + g.h * 0.75]
            : [g.x, g.y + g.h * 0.25, g.x - out, g.y + g.h * 0.25, g.x - out, g.y + g.h * 0.75, g.x, g.y + g.h * 0.75]
          : far
            ? [g.x + g.w * 0.3, g.y + g.h, g.x + g.w * 0.3, g.y + g.h + out, g.x + g.w * 0.7, g.y + g.h + out, g.x + g.w * 0.7, g.y + g.h]
            : [g.x + g.w * 0.3, g.y, g.x + g.w * 0.3, g.y - out, g.x + g.w * 0.7, g.y - out, g.x + g.w * 0.7, g.y];
      }
      const k = far ? 1 : -1;
      return bezierPoints(
        vertical
          ? [far ? g.x + g.w : g.x, g.y + g.h * 0.35, (far ? g.x + g.w : g.x) + k * r * 1.6, g.y + g.h * 0.2, (far ? g.x + g.w : g.x) + k * r * 1.6, g.y + g.h * 0.8, far ? g.x + g.w : g.x, g.y + g.h * 0.65]
          : [g.x + g.w * 0.35, far ? g.y + g.h : g.y, g.x + g.w * 0.2, (far ? g.y + g.h : g.y) + k * r * 1.6, g.x + g.w * 0.8, (far ? g.y + g.h : g.y) + k * r * 1.6, g.x + g.w * 0.65, far ? g.y + g.h : g.y],
      );
    };
    const a = loop(true);
    const b = loop(false);
    const against = (q: number[]) => placed.reduce((s, p) => s + polyCrossings(p, q), 0);
    const pts = against(b) < against(a) ? b : a;
    placed.push(pts);
    const geo = edgeGeo(e, pts, measure, orthogonal, avoid);
    if (geo.label) {
      // the label goes just outside the loop, not over its short legs
      const lb = geo.label;
      const xs = pts.filter((_, i) => i % 2 === 0);
      const ys = pts.filter((_, i) => i % 2 === 1);
      if (vertical) {
        lb.x = pts === a ? Math.max(...xs) + 4 : Math.min(...xs) - lb.w - 4;
        lb.y = g.y + g.h / 2 - lb.h / 2;
      } else {
        lb.x = g.x + g.w / 2 - lb.w / 2;
        lb.y = pts === a ? Math.max(...ys) + 4 : Math.min(...ys) - lb.h - 4;
      }
    }
    edgeItems.push(geo);
  });

  items.push(...groupItems, ...edgeItems, ...geoNodes.values());
  const g = finish('flow', items);
  g.links = orthogonal ? 'orthogonal' : 'curved';
  return g;
}

/** Proper crossings between two polylines (bounding boxes first: most pairs are far apart). */
function polyCrossings(p: number[], q: number[]): number {
  const box = (r: number[]) => {
    let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
    for (let i = 0; i < r.length; i += 2) {
      x0 = Math.min(x0, r[i]);
      x1 = Math.max(x1, r[i]);
      y0 = Math.min(y0, r[i + 1]);
      y1 = Math.max(y1, r[i + 1]);
    }
    return [x0, y0, x1, y1];
  };
  const a = box(p);
  const b = box(q);
  if (a[2] < b[0] || b[2] < a[0] || a[3] < b[1] || b[3] < a[1]) return 0;
  return countCrossings([p, q]);
}

/** Number of proper crossings between segments of different polylines. */
function countCrossings(routes: number[][]): number {
  const segs: number[][] = [];
  routes.forEach((p, id) => {
    for (let i = 0; i + 3 < p.length; i += 2) segs.push([Math.min(p[i], p[i + 2]), Math.min(p[i + 1], p[i + 3]), Math.max(p[i], p[i + 2]), Math.max(p[i + 1], p[i + 3]), p[i], p[i + 1], p[i + 2], p[i + 3], id]);
  });
  const side = (ox: number, oy: number, ax: number, ay: number, bx: number, by: number) => {
    const v = (ax - ox) * (by - oy) - (ay - oy) * (bx - ox);
    return v > 1e-6 ? 1 : v < -1e-6 ? -1 : 0;
  };
  let n = 0;
  for (let i = 0; i < segs.length; i++) {
    const a = segs[i];
    for (let j = i + 1; j < segs.length; j++) {
      const b = segs[j];
      if (a[8] === b[8] || a[2] < b[0] || b[2] < a[0] || a[3] < b[1] || b[3] < a[1]) continue;
      const d1 = side(b[4], b[5], b[6], b[7], a[4], a[5]);
      const d2 = side(b[4], b[5], b[6], b[7], a[6], a[7]);
      const d3 = side(a[4], a[5], a[6], a[7], b[4], b[5]);
      const d4 = side(a[4], a[5], a[6], a[7], b[6], b[7]);
      if (d1 * d2 < 0 && d3 * d4 < 0) n++;
    }
  }
  return n;
}

/** Drop middle points of straight runs. */
function simplifyStraight(p: number[]): number[] {
  const out: number[] = [p[0], p[1]];
  for (let i = 2; i < p.length; i += 2) {
    const n = out.length;
    if (n >= 4) {
      const [ax, ay, bx, by] = [out[n - 4], out[n - 3], out[n - 2], out[n - 1]];
      if ((Math.abs(ax - bx) < 0.01 && Math.abs(bx - p[i]) < 0.01) || (Math.abs(ay - by) < 0.01 && Math.abs(by - p[i + 1]) < 0.01)) {
        out[n - 2] = p[i];
        out[n - 1] = p[i + 1];
        continue;
      }
    }
    out.push(p[i], p[i + 1]);
  }
  return out;
}

function reversePoints(p: number[]): number[] {
  const out: number[] = [];
  for (let i = p.length - 2; i >= 0; i -= 2) out.push(p[i], p[i + 1]);
  return out;
}

function bezierPoints(c: number[]): number[] {
  const out: number[] = [];
  for (let k = 0; k <= 16; k++) {
    const t = k / 16;
    const u = 1 - t;
    out.push(u * u * u * c[0] + 3 * u * u * t * c[2] + 3 * u * t * t * c[4] + t * t * t * c[6], u * u * u * c[1] + 3 * u * u * t * c[3] + 3 * u * t * t * c[5] + t * t * t * c[7]);
  }
  return out;
}

/** Middle of the longest segment that is not the first or last one (the run across a gap), if any. */
function longestRun(p: number[]): [number, number] | null {
  let best: [number, number] | null = null;
  let len = 24;
  for (let i = 2; i + 2 < p.length - 2; i += 2) {
    const l = Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]);
    if (l > len) {
      len = l;
      best = [(p[i] + p[i + 2]) / 2, (p[i + 1] + p[i + 3]) / 2];
    }
  }
  return best;
}

/** Point at fraction `f` of the length of a polyline. */
function pointAlong(p: number[], f: number): [number, number] {
  let total = 0;
  for (let i = 2; i < p.length; i += 2) total += Math.hypot(p[i] - p[i - 2], p[i + 1] - p[i - 1]);
  let acc = 0;
  for (let i = 2; i < p.length; i += 2) {
    const seg = Math.hypot(p[i] - p[i - 2], p[i + 1] - p[i - 1]);
    if (acc + seg >= total * f && seg > 0) {
      const t = (total * f - acc) / seg;
      return [p[i - 2] + (p[i] - p[i - 2]) * t, p[i - 1] + (p[i + 1] - p[i - 1]) * t];
    }
    acc += seg;
  }
  return [p[0], p[1]];
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const overlaps = (a: Rect, b: Rect, gap = 3) => a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;

/**
 * Label spot on an angular link: the last leg into the target first (every link has its own), then
 * the other legs from the end; on each leg the middle first, then towards its ends. A spot must
 * fit on the leg (clear of the arrowhead) and must not cover a node, a group title or another label.
 */
function labelSpot(p: number[], w: number, h: number, avoid: Rect[]): [number, number] | null {
  const last = p.length / 2 - 2;
  // on the line first; then right beside it (a vertical leg: to its right or left, a horizontal one:
  // above or below it)
  for (const side of [0, 1, -1]) {
    for (let s = last; s >= 0; s--) {
      const [ax, ay, bx, by] = [p[2 * s], p[2 * s + 1], p[2 * s + 2], p[2 * s + 3]];
      const len = Math.hypot(bx - ax, by - ay);
      const horizontal = Math.abs(bx - ax) > Math.abs(by - ay);
      const along = side ? 0 : horizontal ? w : h;
      const m0 = 6;
      const m1 = s === last ? 14 : 6;
      const free = len - along - m0 - m1;
      if (len < 1 || free < 0) continue;
      const ox = horizontal ? 0 : side * (w / 2 + 5);
      const oy = horizontal ? side * -(h / 2 + 4) : 0;
      for (const t of [0.5, 0.3, 0.7, 0.12, 0.88, 0, 1]) {
        const d = (m0 + along / 2 + free * t) / len;
        const cx = ax + (bx - ax) * d + ox;
        const cy = ay + (by - ay) * d + oy;
        const r = { x: cx - w / 2, y: cy - h / 2, w, h };
        if (!avoid.some((o) => overlaps(r, o))) return [cx, cy];
      }
    }
  }
  return null;
}

function edgeGeo(e: FlowModel['edges'][number], points: number[], measure: Measure, orthogonal = false, avoid: Rect[] = [], lineBoxes: Rect[] = []): GeoEdge {
  const arrowEnd = e.kind === 'arrow' || e.kind === 'both' || e.kind === 'dotted-arrow' || e.kind === 'thick';
  const geo: GeoEdge = {
    k: 'edge',
    from: e.from,
    to: e.to,
    points,
    arrowStart: e.kind === 'both',
    arrowEnd,
    dash: strokeDash(e.style, e.kind === 'dotted' || e.kind === 'dotted-arrow'),
    width: e.style.width ?? (e.kind === 'thick' ? 3 : THEME.edge.width),
    color: e.style.stroke ?? THEME.edge.color,
  };
  if (e.label) {
    const font = THEME.smallFont;
    const lines = e.label.split('\n');
    const w = Math.max(...lines.map((l) => measure(l, font, false))) + 12;
    const h = lines.length * font * LINE_H + 6;
    // orthogonal: a free spot, preferably on the last leg; curved: a bit before the middle
    // clear of other links' lines if possible, otherwise at least clear of nodes, titles and labels
    const [x, y] = (orthogonal ? labelSpot(points, w, h, [...avoid, ...lineBoxes]) ?? labelSpot(points, w, h, avoid) ?? longestRun(points) : null) ?? pointAlong(points, 0.42);
    geo.label = { text: e.label, x: x - w / 2, y: y - h / 2, w, h, fontSize: font, color: e.style.text ?? THEME.edge.label };
    avoid.push({ x: geo.label.x, y: geo.label.y, w, h });
  }
  return geo;
}

/** Shift everything to a margin and compute the size. */
export function finish(kind: Geometry['kind'], items: GeoItem[]): Geometry {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const add = (x: number, y: number, w = 0, h = 0) => {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x + w);
    y1 = Math.max(y1, y + h);
  };
  for (const it of items) {
    if (it.k === 'edge') {
      for (let i = 0; i < it.points.length; i += 2) add(it.points[i] - 6, it.points[i + 1] - 6, 12, 12);
      if (it.label) add(it.label.x, it.label.y, it.label.w, it.label.h);
    } else add(it.x, it.y, it.w, it.h);
  }
  if (!items.length) return { kind, width: 200, height: 80, items };
  const dx = MARGIN - x0;
  const dy = MARGIN - y0;
  for (const it of items) {
    if (it.k === 'edge') {
      for (let i = 0; i < it.points.length; i += 2) {
        it.points[i] += dx;
        it.points[i + 1] += dy;
      }
      if (it.label) {
        it.label.x += dx;
        it.label.y += dy;
      }
    } else {
      it.x += dx;
      it.y += dy;
      if (it.k === 'frame') for (const s of it.sections) s.y += dy;
    }
  }
  return { kind, width: Math.ceil(x1 - x0 + MARGIN * 2), height: Math.ceil(y1 - y0 + MARGIN * 2), items };
}
